import axios from 'axios';
import { BASE_URL, AGE_RATING_MAP_API_PATH } from '../../../constants/api';
import { FetchResult } from '../../type/apiType';

export interface ServiceAgeRatingMap {
  id: number;
  service_name: string;
  /** US 등급 코드 -> 해당 서비스 등급 코드. 예: { "TV-14": "VN_T16" } */
  mapping: Record<string, string>;
  is_active: boolean;
}

/** service_name -> (US 코드 -> 해당 서비스 코드) */
export type AgeRatingMaps = Record<string, Record<string, string>>;

/**
 * 서비스별 age rating 변환표.
 *
 * 등급은 CMS 에서 US 기준으로만 설정하고, sync 가 이 표로 각 서비스 코드로 바꿔
 * 전달한다. 화면에 대응 등급을 병기할 때 표를 프론트에 하드코딩하면 실제 sync
 * 결과와 갈라질 수 있으므로, 반드시 같은 출처인 이 API 에서 읽는다.
 */
class AgeRatingMapApiService {
  private cache: AgeRatingMaps | null = null;

  private inFlight: Promise<AgeRatingMaps> | null = null;

  /**
   * 활성 변환표 전체를 가져온다.
   *
   * 등급 드롭다운은 여러 화면에서 열리지만 표는 거의 바뀌지 않으므로 한 번만
   * 받아서 재사용한다. 동시에 여러 번 불려도 요청은 하나만 나간다.
   */
  public async fetchMaps(): Promise<AgeRatingMaps> {
    if (this.cache) return this.cache;
    if (this.inFlight) return this.inFlight;

    this.inFlight = axios
      .get<FetchResult<ServiceAgeRatingMap> | ServiceAgeRatingMap[]>(
        `${BASE_URL}${AGE_RATING_MAP_API_PATH}`
      )
      .then(response => {
        const data = response.data;
        const rows: ServiceAgeRatingMap[] = Array.isArray(data) ? data : data.results;
        const maps: AgeRatingMaps = {};
        rows
          .filter(row => row.is_active && row.mapping)
          .forEach(row => {
            maps[row.service_name] = row.mapping;
          });
        this.cache = maps;
        return maps;
      })
      .catch(() => {
        // 표를 못 받아도 등급 설정 자체는 되어야 하므로, 빈 표로 두고 기존
        // 라벨(US + ODKR)만 보여준다. 캐시에 넣지 않아 다음 열 때 다시 시도한다.
        return {};
      })
      .finally(() => {
        this.inFlight = null;
      });

    return this.inFlight;
  }

  public clearCache(): void {
    this.cache = null;
  }
}

export const ageRatingMapApiService = new AgeRatingMapApiService();
